import { useContext, useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Modal from '../../components/modal';
import Pagination from '../../components/Pagination';
import Switch from '../../components/Switch';
import { listCategories, createCategory, updateCategory, removeCategory } from '../../utils/queries';

const PAGE_SIZE = 20;
const emptyForm = { name: '', sortOrder: 0, featuredFlag: false };

export default function CategoriesList() {
	const { activeStoreId } = useContext(MyContext);
	const [categories, setCategories] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listCategories({
			storeId: activeStoreId,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
		}).then((result) => {
			if (result?.success) {
				setCategories(result.data.categories);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, page, search]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setError('');
		setOpen(true);
	};

	const openEdit = (category) => {
		setEditing(category);
		setForm({ name: category.name, sortOrder: category.sortOrder, featuredFlag: !!category.featuredFlag });
		setError('');
		setOpen(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const result = editing
			? await updateCategory(editing.categoryId, form)
			: await createCategory({ ...form, storeId: activeStoreId });
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setOpen(false);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeCategory(id);
		refresh();
	};

	const columns = [
		{
			key: 'name',
			header: 'Name',
			render: (value, row) => (
				<button type='button' className='text-black font-medium hover:underline' onClick={() => openEdit(row)}>
					{value}
				</button>
			),
		},
		{ key: 'slug', header: 'Slug' },
		{ key: 'sortOrder', header: 'Sort Order' },
		{ key: 'featuredFlag', header: 'Featured', render: (value) => (value ? 'Yes' : '') },
		{
			key: 'categoryId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={() => handleRemove(id)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Categories</h1>
				<button type='button' className='btn-primary' disabled={!activeStoreId} onClick={openCreate}>
					New Category
				</button>
			</div>

			<div className='mb-5'>
				<label>Search</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={categories} loading={loading} emptyHeading='No categories yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={open} setOpen={setOpen} title={editing ? 'Edit Category' : 'New Category'}>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<div>
						<label>Name</label>
						<input
							className='form-input'
							required
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
						/>
					</div>
					<div className="w-1/5">
						<label>Sort Order</label>
						<input
							type='number'
							className='form-input'
							value={form.sortOrder}
							onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
						/>
					</div>
					<Switch
						checked={form.featuredFlag}
						onChange={(checked) => setForm({ ...form, featuredFlag: checked })}
						label='Featured'
					/>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
